<template>
  <div>
    <admin-template>
      <div class="max-w-lg mx-auto mt-12 px-5">

        <!-- Header -->
        <div class="flex items-center gap-3 mb-8">
          <div class="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center shrink-0">
            <svg class="text-white" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </div>
          <div>
            <h1 class="text-xl font-black text-gray-900 leading-tight">Upload de fichier</h1>
            <p class="text-sm text-gray-400">Déposez un document et récupérez son lien instantanément.</p>
          </div>
        </div>

        <!-- Upload card -->
        <div class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

          <!-- Upload zone -->
          <div class="p-6 border-b border-gray-100">
            <p class="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Fichier</p>
            <input-file
              v-model="uploadedFile"
              key-name="tools"
              :preview="false"
            />
          </div>

          <!-- Result (appears when URL is ready) -->
          <transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 -translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
          >
            <div v-if="uploadedFile" class="p-6">

              <!-- Success banner -->
              <div class="flex items-center gap-2.5 bg-orange-50 border border-orange-100 rounded-xl px-4 py-3 mb-5">
                <div class="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center shrink-0">
                  <svg class="text-white" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <p class="text-sm font-semibold text-orange-700">Fichier uploadé avec succès !</p>
              </div>

              <!-- URL display -->
              <p class="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Lien d'accès</p>
              <div class="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-4">
                <svg class="text-gray-400 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                <span class="flex-1 font-mono text-sm text-gray-700 truncate min-w-0" :title="uploadedFile">{{ uploadedFile }}</span>
              </div>

              <!-- Action buttons -->
              <div class="flex gap-2">
                <button
                  class="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-bold text-white transition-all duration-150 active:scale-95"
                  :class="copied ? 'bg-orange-500' : 'bg-gray-900 hover:bg-gray-800'"
                  @click="copyUrl"
                >
                  <transition name="icon-swap" mode="out-in">
                    <svg v-if="!copied" key="copy" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    <svg v-else key="check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </transition>
                  {{ copied ? 'Copié !' : 'Copier le lien' }}
                </button>

                <a
                  :href="uploadedFile"
                  target="_blank"
                  rel="noopener"
                  class="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-700 hover:border-orange-400 hover:text-orange-500 transition-all duration-150 no-underline active:scale-95"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                  Ouvrir
                </a>
              </div>

              <!-- Reset -->
              <button
                class="w-full mt-3 flex items-center justify-center gap-1.5 py-2 text-sm text-gray-400 font-medium hover:text-orange-500 transition-colors duration-150"
                @click="uploadedFile = null"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="1 4 1 10 7 10"/>
                  <path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
                </svg>
                Uploader un autre fichier
              </button>

            </div>
          </transition>
        </div>

      </div>

      <!-- Toast -->
      <transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-2"
      >
        <div
          v-if="copied"
          class="fixed bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-5 py-3 rounded-full shadow-xl z-50 pointer-events-none whitespace-nowrap"
        >
          <svg class="text-orange-400" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Lien copié dans le presse-papier
        </div>
      </transition>

    </admin-template>
  </div>
</template>

<script>
export default {
  data() {
    return {
      uploadedFile: null,
      copied: false,
    }
  },

  methods: {
    async copyUrl() {
      await navigator.clipboard.writeText(this.uploadedFile)
      this.copied = true
      setTimeout(() => { this.copied = false }, 2500)
    },
  },
}
</script>

<style scoped>
.icon-swap-enter-active, .icon-swap-leave-active { transition: opacity 0.15s, transform 0.15s; }
.icon-swap-enter-from { opacity: 0; transform: scale(0.6); }
.icon-swap-leave-to   { opacity: 0; transform: scale(1.3); }
</style>
